import { BasicResponseDTO } from '@dto/basicResponse.dto';
import axios, { AxiosError, AxiosHeaders, HttpStatusCode, InternalAxiosRequestConfig } from 'axios';

/** User-facing text for failures that never produced an HTTP response, keyed by axios error code */
export const NETWORK_ERROR_MESSAGES: Partial<Record<string, string>> = {
  [AxiosError.ERR_NETWORK]: 'Backend is unreachable',
  [AxiosError.ECONNABORTED]: 'Request timed out',
  [AxiosError.ETIMEDOUT]: 'Request timed out',
};

/** HTTP status axios reports when no response arrived, mirrors XMLHttpRequest */
export const NO_RESPONSE_STATUS = 0;

/**
 * Gives a network-level failure a synthetic response so `error.response.data.message` is always safe to read
 * @param error rejected axios error, mutated in place; cancellations and real responses are left untouched
 */
export function normalizeNetworkError(error: AxiosError<BasicResponseDTO>) {
  if (error.response || axios.isCancel(error)) return;
  const message = NETWORK_ERROR_MESSAGES[error.code] ?? error.message;
  error.response = {
    status: NO_RESPONSE_STATUS,
    statusText: error.code ?? '',
    headers: new AxiosHeaders(),
    config: error.config ?? ({ headers: new AxiosHeaders() } as InternalAxiosRequestConfig),
    data: { statusCode: NO_RESPONSE_STATUS, message, error: error.code },
  };
}

/** Statuses meaning the backend could not be reached: no response, or a proxy answering for a backend that is down or restarting */
export const UNREACHABLE_STATUSES: readonly number[] = [NO_RESPONSE_STATUS, HttpStatusCode.BadGateway, HttpStatusCode.ServiceUnavailable, HttpStatusCode.GatewayTimeout];

/**
 * True when the failure means the backend is unreachable rather than the request being wrong or the backend erroring
 * @param reason rejection from an axios call, after the interceptor normalised it
 */
export function isBackendUnavailable(reason: unknown): reason is AxiosError<BasicResponseDTO> {
  if (!axios.isAxiosError(reason) || axios.isCancel(reason)) return false;
  return UNREACHABLE_STATUSES.includes(reason.response?.status);
}

/**
 * Text to show for a failed request: the server's message when there is one, otherwise the error's own
 * @param reason anything a request promise rejected with
 */
export function getErrorMessage(reason: unknown): string {
  if (axios.isAxiosError<BasicResponseDTO>(reason)) return reason.response?.data?.message ?? reason.message;
  return reason instanceof Error ? reason.message : String(reason);
}
